// custom (2026-08-07): a paleta dos GRÁFICOS. Fonte única — nenhum componente inventa cor.
//
// ── Por que gráfico tem paleta própria ────────────────────────────────────────────────
// A interface do produto é tinta + amarelo de marca, e o amarelo significa "clique aqui".
// Gráfico é outro vocabulário: ali a cor não convida a ação, ela DISTINGUE séries. O
// Gustavo trouxe a referência da Attio e passou estas nove cores.
//
// ── A ordem não é a ordem em que ele mandou, e isso é de propósito ────────────────────
// Um gráfico de três séries usa os três primeiros slots. Se dois vizinhos de slot forem
// parecidos, o gráfico nasce ilegível — e "parecido" inclui quem enxerga cor de outro
// jeito. Rodei as 5.040 ordens possíveis medindo, para cada tamanho de gráfico, a menor
// distância CIEDE2000 entre as séries usadas — em visão normal e nas três daltonias
// (protanopia, deuteranopia, tritanopia) — e escolhi a ordem que maximiza a pior delas.
//
// Resultado medido com as SEIS cores atuais (pior caso entre os quatro tipos de visão):
//   2 séries ΔE 50,0 · 3 séries 24,0 · 4 séries 21,0   ← acima do alvo 8
//   5 séries ΔE 7,2 (ciano/lilás, limítrofe) · 6 séries 3,3 (ciano/orquídea, reprova)
// A saída do laranja custou exatamente isso: antes o conjunto aguentava 5 séries com folga.
// Nenhum gráfico do CRM chega a 5 categorias hoje, então o preço está pago — mas se um dia
// chegar, o par a separar é ciano/lilás, e a saída é trocar o lilás por um verde.
//
// ── O amarelo está barrado dos três primeiros slots ───────────────────────────────────
// `#FFD241` está a ΔE 3,3 do amarelo de marca `#FFD400` — para o olho, a mesma cor. Como
// slot 1, todo gráfico de série única sairia inteiro na cor de botão do produto e o
// amarelo perderia o significado de affordance que carrega em todas as outras telas.
// Barrando-o dos três primeiros, ele só aparece quando já há quatro séries em cena — aí o
// contexto é claramente de dado, não de ação. A ordem abaixo é a melhor sob essa restrição.
//
// ── Contraste: por que estas cores só valem como ÁREA ─────────────────────────────────
// Contra o branco, só laranja/azul/rosa passam de 3:1. Ciano, amarelo, lilás e orquídea
// ficam abaixo — o que as proíbe em traço fino de 1px e em texto colorido, e as libera em
// barra, fatia de rosca e área preenchida, que é como a Attio as usa. Toda leitura de
// categoria vem do RÓTULO ESCRITO ao lado, nunca da cor sozinha.
// Tinta escura (#1C2024) sobre qualquer uma delas passa 4,5:1 — rótulo dentro da barra é
// seguro. A exceção é o azul (3,57:1): sobre azul, rótulo em branco.

// ⚠️ O LARANJA SAIU (2026-08-08). Decisão do Gustavo, direta: "tira o laranja, eu não gosto
// de laranja, é bem feio. Utiliza mais o amarelo, rosa." Não é preferência sem custo — com
// seis cores em vez de sete, a sétima categoria passa a virar "Outras" mais cedo. É um preço
// pequeno e ele está pago: nenhum gráfico do CRM chega a seis categorias hoje.
// A ordem foi refeita com a mesma medida de antes (distância entre séries em visão normal e
// nas três daltonias), agora com o amarelo e o rosa puxados para a frente, como ele pediu.
export const PALETA_SERIES = Object.freeze([
  { nome: 'amarelo', cor: '#FFD241' },
  { nome: 'azul', cor: '#286EF0' },
  { nome: 'rosa', cor: '#F55384' },
  { nome: 'ciano', cor: '#61C9E4' },
  { nome: 'lilas', cor: '#B596FC' },
  { nome: 'orquidea', cor: '#E2A6E2' },
]);

// Quantas categorias o gráfico pode colorir antes de agrupar o resto.
export const MAX_SERIES = 6;

// ── Subconjunto para TRAÇO FINO (linha, contorno) ─────────────────────────────────────
// A ordem acima resolve "distinguir categorias"; não resolve "sobreviver como linha de
// 1,5px sobre branco", que é outro problema. Medido contra #FFFFFF:
//   azul 4,59:1 · rosa 3,25:1 · laranja 2,19 · lilás 2,40 · orquídea 1,95 · ciano 1,91 · amarelo 1,44
// Só os dois primeiros passam do mínimo de 3:1. Uma linha de ciano sobre branco não é uma
// escolha ruim de estilo — é uma linha que some. Gráfico de LINHA usa esta lista; gráfico
// de barra, área e rosca usa a lista completa, porque ali a cor é superfície e não fio.
export const PALETA_TRACO = Object.freeze(['#286EF0', '#F55384']);

// O cinza das séries de apoio em gráfico de linha (contexto, não protagonista).
export const CINZA_APOIO = '#6B7A8C';
export const CINZA_APOIO_FRACO = '#AAB4BF';

// A cor de "Outras" — cinza de propósito: agrupamento não é uma categoria, é o que sobrou.
export const COR_OUTRAS = '#C9C5BD';

// ── Comparativo (período anterior) ────────────────────────────────────────────────────
// Na referência da Attio o período anterior NÃO gasta uma cor: ele aparece como uma barra
// "fantasma" atrás da atual — preenchimento claro do mesmo matiz e contorno tracejado.
// Período não é categoria; se consumisse um slot sobrariam menos cores para o que importa.
// Estes são os dois tons claros que o Gustavo passou, e é exatamente esse o trabalho deles.
export const FANTASMA = Object.freeze({
  rosa: '#FDC4D4',
  creme: '#FFEDBA',
  // Para os matizes sem tom claro próprio, o fantasma é a própria cor da série a 22% —
  // mesma leitura (mesmo matiz, muito mais claro) sem inventar hex novo.
  opacidade: 0.22,
});

/** Cor do slot `i` (0-based). Do 7º em diante devolve o cinza de "Outras". */
export function corDaSerie(i) {
  if (i >= MAX_SERIES) return COR_OUTRAS;
  return PALETA_SERIES[i].cor;
}

/** As `n` primeiras cores, já com "Outras" no lugar do excedente. */
export function coresParaSeries(n) {
  return Array.from({ length: n }, (_, i) => corDaSerie(i));
}

/** `#RRGGBB` + alfa → `rgba(...)`. Usado pelo fantasma e pelos gradientes de área. */
export function comAlfa(hex, alfa) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alfa})`;
}

/**
 * O par (cor cheia, cor fantasma) de um slot — para o gráfico de barra comparativa.
 * O fantasma dos slots que têm tom claro próprio usa o hex passado pelo Gustavo; os demais
 * derivam da própria série em alfa, o que mantém o matiz idêntico.
 */
export function parComparativo(i) {
  const cor = corDaSerie(i);
  const nome = i < MAX_SERIES ? PALETA_SERIES[i].nome : 'outras';
  if (nome === 'rosa') return { cor, fantasma: FANTASMA.rosa };
  if (nome === 'amarelo') return { cor, fantasma: FANTASMA.creme };
  return { cor, fantasma: comAlfa(cor, FANTASMA.opacidade) };
}
